import { Link } from '../../../components/Link';
import {
  Lead,
  LearnCode,
  LearnList,
  LearnPageLayout,
  LearnSection,
  LearnSubsection,
  LearnTable,
  Tok,
} from './LearnPageLayout';
import { learnArticle } from './learnArticles';

const meta = learnArticle('email-authentication');

/**
 * PILLAR of the authentication cluster. Its job is orientation, not depth: name the three records,
 * say what each one actually proves, and hand off to the spoke that goes deep. The near-duplicate
 * guard is the check on that discipline — if this page starts re-explaining what /learn/spf-record/
 * explains, the two converge and CI says so.
 */
export default function EmailAuthenticationPage() {
  return (
    <LearnPageLayout
      content={{
        meta,
        definition:
          'Email authentication is three DNS records working together. SPF lists which servers may send for your domain. DKIM signs each message so a receiver can prove it was not altered. DMARC ties both to the address readers actually see, and tells receivers what to do when neither lines up. Only DMARC carries an instruction.',
        faq: FAQ,
        body: <Body />,
      }}
    />
  );
}

function Body() {
  return (
    <>
      <LearnSection title="Three records, three different jobs">
        <p>
          They are usually presented as a checklist, which hides the thing worth understanding:
          each record answers a different question, and only one of them tells the receiver what to
          do about the answer.
        </p>
        <LearnTable
          head={['Record', 'Question it answers', 'Lives at', 'Carries an instruction?']}
          rows={[
            [
              'SPF',
              'Is this server allowed to send for this domain?',
              <Tok key="1">yourdomain.com</Tok>,
              'Advisory only',
            ],
            [
              'DKIM',
              'Was this message signed by the domain, and unaltered since?',
              <Tok key="2">selector._domainkey.yourdomain.com</Tok>,
              'No',
            ],
            [
              'DMARC',
              'Does either of the above line up with the visible From: address?',
              <Tok key="3">_dmarc.yourdomain.com</Tok>,
              <Lead key="4">Yes</Lead>,
            ],
          ]}
        />
        <p>
          That last column is the whole point. A receiver seeing an SPF failure has no mandate to do
          anything about it — SPF’s own qualifiers are a recommendation. DMARC is the only record
          that says <em>reject this</em>, and it is the only one that concerns itself with the
          address a human actually reads.
        </p>
      </LearnSection>

      <LearnSection title="The order they run in">
        <p>
          A receiver evaluates all three on the same message, in roughly this order. Understanding
          the sequence is what makes a failure report legible.
        </p>
        <LearnList ordered>
          <li>
            <Lead>SPF checks the envelope.</Lead> The connecting IP is compared against the record
            at the <Tok>Return-Path</Tok> domain — which is frequently your ESP’s domain, not
            yours. Result: pass, fail, softfail, neutral, permerror or temperror.
          </li>
          <li>
            <Lead>DKIM verifies the signature.</Lead> The receiver reads <Tok>s=</Tok> and{' '}
            <Tok>d=</Tok> from the <Tok>DKIM-Signature</Tok> header, fetches the key, and
            recomputes the hash. Result: pass or fail, per signature — a message can carry several.
          </li>
          <li>
            <Lead>DMARC checks alignment, then applies policy.</Lead> It asks whether an SPF or
            DKIM <em>pass</em> came from a domain that matches the <Tok>From:</Tok> header. If
            neither does, it applies whatever your record says: nothing, quarantine, or reject.
          </li>
        </LearnList>
        <p>
          The step people skip is the third one’s first half. DMARC does not care that SPF passed.
          It cares that SPF passed <em>for the right domain</em>. That distinction accounts for most
          confused DMARC reports, and it has{' '}
          <Link to="/learn/dmarc-alignment" className="text-accent underline underline-offset-4">
            its own page
          </Link>
          .
        </p>
      </LearnSection>

      <LearnSection title="What a full set looks like">
        <p>Three records for a domain sending through Google Workspace and one ESP:</p>
        <LearnCode>
          {`yourdomain.com                TXT   "v=spf1 include:_spf.google.com include:sendgrid.net ~all"
google._domainkey.yourdomain.com   TXT   "v=DKIM1; k=rsa; p=MIIBIjANBg…"
s1._domainkey.yourdomain.com       CNAME s1.domainkey.u123.wl.sendgrid.net
_dmarc.yourdomain.com              TXT   "v=DMARC1; p=none; rua=mailto:dmarc@yourdomain.com"`}
        </LearnCode>
        <p>
          One SPF record covering both senders, one DKIM key per sender, one DMARC policy for the
          domain. That shape does not change as you add senders — you add an{' '}
          <Tok>include:</Tok> and a selector, never a second SPF or DMARC record.
        </p>
      </LearnSection>

      <LearnSection title="What Gmail and Yahoo now require">
        <p>
          In February 2024 Gmail and Yahoo made authentication a condition of delivery rather than
          a ranking signal. The requirements differ by volume, and the threshold is per-day, not
          per-campaign.
        </p>
        <LearnTable
          head={['Requirement', 'Under 5,000/day', '5,000+/day']}
          rows={[
            ['SPF or DKIM', 'Required', 'Required'],
            ['SPF and DKIM both', '—', 'Required'],
            ['DMARC record published', '—', <Lead key="a">Required (p=none is enough)</Lead>],
            ['Alignment with the From: domain', '—', 'Required'],
            ['One-click unsubscribe (RFC 8058)', '—', 'Required'],
            ['Spam complaint rate', 'Below 0.3%', 'Below 0.3%'],
            ['Valid forward and reverse DNS on the sending IP', 'Required', 'Required'],
            ['TLS on the connection', 'Required', 'Required'],
          ]}
        />
        <p>
          Two things people miss. The 5,000 threshold counts messages to that provider, per day,
          from your domain — so one large campaign puts you in the strict tier for that day.
          And the complaint-rate ceiling is not an authentication requirement at all; it is a
          reputation one, which is why{' '}
          <Link to="/learn/dkim-selector" className="text-accent underline underline-offset-4">
            getting the records right
          </Link>{' '}
          is necessary and not sufficient.
        </p>
      </LearnSection>

      <LearnSection title="Which one is failing?">
        <p>Symptom to record, for the common cases:</p>
        <LearnTable
          head={['What you see', 'Usually', 'Where to look']}
          rows={[
            [
              'Mail fine directly, fails when forwarded',
              'SPF — forwarding changes the connecting IP',
              <Link key="a" to="/learn/dkim" className="text-accent underline underline-offset-4">DKIM</Link>,
            ],
            [
              'One sending service fails, the rest pass',
              'That service’s DKIM key was rotated, or its include was dropped',
              <Link key="b" to="/learn/dkim-selector" className="text-accent underline underline-offset-4">Selectors</Link>,
            ],
            [
              'Everything failed at once, overnight, no change made',
              'SPF crossed the 10-lookup limit when a provider changed their record',
              <Link key="c" to="/learn/spf-too-many-dns-lookups" className="text-accent underline underline-offset-4">Lookup limit</Link>,
            ],
            [
              'SPF passes, DMARC fails',
              'Alignment — SPF passed for the ESP’s domain, not yours',
              <Link key="d" to="/learn/dmarc-alignment" className="text-accent underline underline-offset-4">Alignment</Link>,
            ],
            [
              'Reports show sources you do not recognise',
              'Either spoofing, or a forgotten internal sender',
              <Link key="e" to="/learn/dmarc" className="text-accent underline underline-offset-4">DMARC</Link>,
            ],
          ]}
        />
      </LearnSection>

      <LearnSection title="The order to fix them in">
        <LearnSubsection title="1. SPF first, because it is the cheapest to get wrong">
          <p>
            One record, one line, and the failure modes are structural rather than subtle: a second
            record, or too many lookups. Both void the whole thing.
          </p>
        </LearnSubsection>
        <LearnSubsection title="2. DKIM next, because DMARC leans on it">
          <p>
            DKIM survives forwarding and SPF does not, so a domain that authenticates on DKIM alone
            is more robust than one that authenticates on SPF alone. If you only have budget for
            one to be reliable, make it this one.
          </p>
        </LearnSubsection>
        <LearnSubsection title="3. DMARC last, at p=none, and leave it there for a while">
          <p>
            Publishing <Tok>p=none</Tok> changes nothing about delivery and starts the aggregate
            reports flowing. Those reports are the only view you will ever get of who is sending as
            your domain. Read them for a few weeks before touching the policy.
          </p>
        </LearnSubsection>
        <p>
          Reversing that order is the classic mistake: <Tok>p=reject</Tok> published before the
          reports have shown you your own forgotten senders, and the invoicing system nobody
          remembered stops delivering.
        </p>
      </LearnSection>
    </>
  );
}

const FAQ = [
  {
    q: 'Do I need all three, or is one enough?',
    a: 'For under 5,000 messages a day to Gmail or Yahoo, SPF or DKIM is the stated minimum. Above that, both plus a DMARC record are required. Practically, publish all three regardless — the marginal effort is small and the volume threshold is per day, so a single large campaign can move you into the strict tier without warning.',
  },
  {
    q: 'Which should I set up first?',
    a: 'SPF, then DKIM, then DMARC at p=none. DMARC last is not optional ordering — it evaluates the other two, so publishing an enforcing policy before they are correct is what causes legitimate mail to stop being delivered.',
  },
  {
    q: 'Can I have more than one SPF or DMARC record?',
    a: 'No. Exactly one of each per domain. Two SPF records is an automatic permerror that voids SPF entirely, and two DMARC records means the policy is ignored. DKIM is the opposite — several keys is normal and correct, one per sending service.',
  },
  {
    q: 'My mail delivers fine. Do I still need this?',
    a: 'Delivering today and being protected are different things. Without DMARC at an enforcing policy, anyone can send mail with your domain in the From: line and receivers have no instruction to refuse it. The reputation damage lands on your domain, not theirs.',
  },
  {
    q: 'How long does DNS take to take effect?',
    a: 'Bounded by the zone TTL, usually minutes and occasionally an hour. If a change has not taken effect after that, the far more likely explanation is that the record was published under the wrong name — check the fully-qualified name your DNS host resolved, not the string you typed into the box.',
  },
  {
    q: 'Does any of this stop my mail landing in spam?',
    a: 'It removes one reason for it. Authentication is a gate, not a ranking boost — passing does not earn you the inbox, but failing reliably costs it. Once the records are right, the remaining causes are reputation, list quality and engagement.',
  },
];
